// ==================== 类型定义 ====================

/**
 * 注册人信息数据结构（对应后端 SrmSupplierAdmissionApplicationRegistration）
 */
interface RegistrantInfo {
  id?: number; // 注册人ID
  admissionApplicationId?: number; // 准入申请ID
  email?: string; // 注册邮箱
  surname?: string; // 姓氏
  name?: string; // 名字
  registrationName?: string; // 名字全称
  deptCode?: string; // 所属部门
  phone?: string; // 电话号码
  createBy?: string; // 创建人
  createTime?: string; // 创建时间
  updateBy?: string; // 更新人
  updateTime?: string; // 更新时间
}

/**
 * 表单返回的平层数据结构
 */
interface FlatFormData {
  [key: string]: any;
}

// ==================== 工具函数 ====================

/**
 * 字段映射：前端表单字段名 -> 注册人对象属性名
 */
const FIELD_NAME_MAP: Record<string, string> = {
  registrationEmail: "email",
  lastName: "surname",
  firstName: "name",
  contactDepartment: "deptCode",
  contactPhone: "phone",
};

/**
 * 将平层的表单数据转换为注册人数组
 *
 * 支持两种模式：
 * 1. 从表单数据创建新的注册人
 * 2. 从表单数据更新现有的注册人（保留 id, admissionApplicationId 等字段）
 *
 * 示例：
 * - 输入: {
 *     firstName_1: "张",
 *     lastName_1: "三",
 *     firstName_2: "李",
 *     lastName_2: "四"
 *   }
 * - 输出: [
 *     { name: "张", surname: "三" },
 *     { name: "李", surname: "四" }
 *   ]
 *
 * @param formValues 表单返回的平层数据
 * @param registrantListLength 原始 registrantList 的长度（用于确定有多少个注册人）
 * @param originalRegistrantList 原始的注册人列表（可选，用于保留 id 等字段）
 * @returns 转换后的注册人数组
 */
export const transformFormDataToRegistrantList = (
  formValues: FlatFormData,
  registrantListLength: number = 1,
  originalRegistrantList?: RegistrantInfo[],
): RegistrantInfo[] => {
  const registrants: RegistrantInfo[] = [];

  // 遍历每个注册人索引
  for (let i = 1; i <= registrantListLength; i++) {
    // 如果有原始数据，保留原有的 id 和 admissionApplicationId
    const originalRegistrant = originalRegistrantList?.[i - 1];
    const registrant: RegistrantInfo = {
      ...(originalRegistrant && {
        id: originalRegistrant.id,
        admissionApplicationId: originalRegistrant.admissionApplicationId,
        createBy: originalRegistrant.createBy,
        createTime: originalRegistrant.createTime,
        updateBy: originalRegistrant.updateBy,
        updateTime: originalRegistrant.updateTime,
      }),
    };

    let hasData = false; // 标记是否有数据

    // 遍历字段映射，提取对应的表单值
    Object.entries(FIELD_NAME_MAP).forEach(
      ([formFieldName, registrantFieldName]) => {
        // 构造可能的字段名（带后缀）
        const fieldWithSuffix = `${formFieldName}_${i}`;
        const fieldWithoutSuffix = formFieldName;

        // 如果只有一个注册人，优先查找无后缀的字段；否则查找有后缀的字段
        let value: any;
        if (registrantListLength === 1) {
          value = formValues[fieldWithoutSuffix];
        } else {
          value = formValues[fieldWithSuffix];
        }

        if (value !== undefined && value !== null && value !== "") {
          registrant[registrantFieldName as keyof RegistrantInfo] = value;
          hasData = true;
        }
      },
    );

    // 只要有原始数据或有新数据，就添加到结果数组
    if (originalRegistrant || hasData) {
      registrants.push(registrant);
    }
  }

  return registrants;
};

/**
 * 从表单数据中移除注册人字段
 * 返回不含注册人字段的对象
 *
 * @param formValues 表单数据
 * @param registrantListLength 注册人数量
 * @returns 移除注册人字段后的对象
 */
export const removeRegistrantFieldsFromFormValues = (
  formValues: FlatFormData,
  registrantListLength: number = 1,
): FlatFormData => {
  const result = { ...formValues };
  const fieldsToRemove = new Set<string>();

  // 收集所有需要移除的字段
  Object.keys(FIELD_NAME_MAP).forEach((fieldName) => {
    if (registrantListLength === 1) {
      fieldsToRemove.add(fieldName);
    } else {
      for (let i = 1; i <= registrantListLength; i++) {
        fieldsToRemove.add(`${fieldName}_${i}`);
      }
    }
  });

  // 移除这些字段
  fieldsToRemove.forEach((field) => {
    delete result[field];
  });

  return result;
};

/**
 * 合并注册人列表和其他表单数据
 *
 * @param formValues 原始表单数据（平层结构）
 * @param registrantList 转换后的注册人数组
 * @param registrantListLength 原始注册人数量
 * @returns 合并后的完整数据
 */
export const mergeFormDataWithRegistrants = (
  formValues: FlatFormData,
  registrantList: RegistrantInfo[],
  registrantListLength: number = 1,
): Record<string, any> => {
  // 移除平层的注册人字段
  const cleanedFormValues = removeRegistrantFieldsFromFormValues(
    formValues,
    registrantListLength,
  );

  // 添加注册人数组
  return {
    ...cleanedFormValues,
    registrantList,
  };
};
